"use client";

import React, { useState, useEffect } from "react";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import {
  getDatabase,
  ref as dbRef,
  set,
  get,
  remove,
  child,
} from "firebase/database";
import imageCompression from "browser-image-compression";
import { Trash2, Upload } from "lucide-react";
import { firebaseApp } from "../config/firebase";
import Modal from "./modal";

const storage = getStorage(firebaseApp);
const auth = getAuth(firebaseApp);
const database = getDatabase(firebaseApp);

function sanitizeKey(path) {
  return path
    .replace(/\//g, "_")
    .replace(/\./g, "_")
    .replace(/#/g, "_")
    .replace(/\$/g, "_")
    .replace(/\[/g, "_")
    .replace(/\]/g, "_")
    .replace(/\s+/g, "_");
}

async function compressImage(originalFile) {
  const options = {
    maxWidthOrHeight: 900, // resize first
    useWebWorker: true,
    fileType: "image/webp",
    initialQuality: 0.85, // start fairly high
  };

  let compressedFile = await imageCompression(originalFile, options);

  // If still > 75 KB, retry with lower quality until under 75 KB
  let quality = 0.8;
  while (compressedFile.size > 75 * 1024 && quality > 0.4) {
    options.initialQuality = quality;
    compressedFile = await imageCompression(originalFile, options);
    quality -= 0.1;
  }

  return compressedFile;
}

export default function ImageManager({
  imgSelector = false,
  onSelect = () => {},
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [optimize, setOptimize] = useState(true);
  const [modalImage, setModalImage] = useState(null);

  const fetchImages = async () => {
    try {
      const snapshot = await get(child(dbRef(database), "uploads"));
      if (snapshot.exists()) {
        const data = Object.values(snapshot.val());
        setImages(data);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  useEffect(() => {
    console.log("useEffect running, fetching images");
    fetchImages();
  }, []);

  const handleUpload = async (e) => {
    if (!e.target.files?.length) return;
    if (!auth.currentUser) {
      alert("You must be logged in to upload images.");
      return;
    }

    const originalFile = e.target.files[0];
    let fileToUpload = originalFile;

    try {
      setLoading(true);

      if (optimize) {
        try {
          fileToUpload = await compressImage(originalFile);
        } catch (err) {
          console.error("Image compression failed:", err);
          fileToUpload = originalFile;
        }
      }

      const filePath = `uploads/${Date.now()}-${originalFile.name}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, fileToUpload);
      const url = await getDownloadURL(storageRef);

      const newImage = {
        url,
        path: filePath,
        name: originalFile.name,
        size: fileToUpload.size,
      };

      const safeKey = sanitizeKey(filePath);
      await set(dbRef(database, `uploads/${safeKey}`), newImage);

      setImages((prev) => [...prev, newImage]);
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed: " + error.message);
    } finally {
      setLoading(false);
      e.target.value = null;
    }
  };

  const handleDelete = async (img) => {
    try {
      const imageRef = ref(storage, img.path);
      await deleteObject(imageRef);

      const safeKey = sanitizeKey(img.path);
      await remove(dbRef(database, `uploads/${safeKey}`));

      setImages((prev) => prev.filter((i) => i.path !== img.path));
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Delete failed: " + error.message);
    }
  };

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center space-x-4">
        <label className="cursor-pointer flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
          <Upload className="w-4 h-4" />
          <span>{loading ? "Uploading..." : "Upload Image"}</span>
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            disabled={loading}
          />
        </label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={optimize}
            onChange={(e) => {
              console.log("Optimize checkbox:", e.target.checked);
              setOptimize(e.target.checked);
            }}
          />
          <span>Optimize before upload</span>
        </label>
      </div>

      <div className="flex justify-between flex-wrap gap-2">
        {images.length === 0 && <p>No images found.</p>}
        {images.map((img) => {
          return (
            <div
              key={img.path}
              className="relative group border rounded-lg overflow-hidden w-[190px]"
            >
              <img
                src={img.url}
                alt={img.name}
                className="w-full h-40 object-cover cursor-pointer"
                onClick={() => setModalImage(img)}
                onError={() => console.error("Failed to load image:", img.url)}
              />
              {img.size && (
                <div className="absolute bottom-0 left-0 bg-black bg-opacity-50 text-white text-xs p-1">
                  {(img.size / 1024).toFixed(2)} KB
                </div>
              )}
              <button
                onClick={() => handleDelete(img)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              {imgSelector ? (
                <button
                  onClick={() => {
                    onSelect(img.url);
                  }}
                  className="absolute px-3 bottom-0 right-0 bg-green-400 hover:bg-successLight text-black text-xs p-1"
                >
                  Select
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {modalImage && (
        <Modal
          size="full"
          show={!!modalImage}
          title={modalImage?.name}
          onClose={() => setModalImage(null)}
        >
          {modalImage && (
            <div className="flex flex-col items-center space-y-2">
              <img
                src={modalImage.url}
                alt={modalImage.name}
                className="max-h-[80vh] w-auto object-contain"
                onError={() =>
                  console.error("Failed to load modal image:", modalImage.url)
                }
              />
              <p>
                Size: {(modalImage.size / 1024).toFixed(2)} KB <br />
                {modalImage.wasCompressed ? "Optimized" : "Original"}
              </p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
